import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import AssistantCitationSchema, { AssistantCitationJSON, AssistantCitationParams } from "ai-assistant/citations/assistant-citation-schema";
import {
  CITATION_SOURCE_KIND_LABELS,
  CitationLocatorKind,
  CitationSourceKind,
  CitationTextSource,
} from "ai-assistant/citations/citation-source-kind";
import articleHeadingAnchor from "knowledge-base/articles/article-heading-anchor";

/** A digit in the excerpt's text, outside its markup: an excerpt that quotes a figure. */
const QUOTED_FIGURE = /\d/;
const MARKUP = /<[^>]*>/g;

export default class AssistantCitation {
  public static create(values: AssistantCitationParams): AssistantCitation {
    const parsed = AssistantCitationSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid AssistantCitation parameters.");
    }
    return new AssistantCitation(parsed);
  }
  
  public static fromJSON(json: AssistantCitationJSON): AssistantCitation {
    if (isNullOrUndefined(json)) return json;
    const params = AssistantCitationSchema.deserialize(json);
    return new AssistantCitation(params);
  }

  /** The number the answer text cites this source by, as [[n]]. */
  public readonly ordinal: number;
  public readonly fileId: Guid; 
  public readonly fileName: string;
  public readonly downloadDocumentId?: Guid;
  public readonly sourcePath?: string;
  /** The physical page the quoted text is on; the file's first page is 1. */
  public readonly page?: number;
  /** What that page has printed on it ("iv", "A-1"), when the document gives one. */
  public readonly pageLabel?: string;
  /** What kind of thing the source is (#5986). Absent when unknown, and on a withheld citation. */
  public readonly sourceKind?: CitationSourceKind;
  /** The knowledge base section, spreadsheet sheet or slide title the text is in (#6019). */
  public readonly locator?: string;
  public readonly locatorKind?: CitationLocatorKind;
  /** How the text was read from its file: only a PDF says (#6019). */
  public readonly textSource?: CitationTextSource;
  /** For text read by OCR, the lowest mean word confidence among its pages, 0 to 100. */
  public readonly ocrConfidence?: number;
  /** For text read by OCR, whether a number on its pages was read with low confidence. */
  public readonly ocrUncertainNumbers?: boolean;
  public readonly nodeId?: Guid;
  public readonly matched?: boolean;
  public readonly text?: string;
  /**
   * Whether the API withheld this source's content because the reader may no longer see it.
   *
   * Only ever set on a conversation read back from history. A citation is a verbatim copy of
   * document content the server re-serves on request, so it re-asks the permission question at
   * read time rather than trusting the answer it had when the answer was written.
   */
  public readonly withheld?: boolean;
  public readonly retrieverScore?: number;
  public readonly semanticScore?: number;

  /**
   * How this citation names its source, or that it cannot.
   *
   * A withheld citation may still name the document when the reader can see it in their
   * documents list - losing content access is not the same as losing sight of the file. When
   * they cannot see it either, the API sends no name and no ids, because naming it would
   * disclose that it exists.
   */
  public get sourceLabel(): string {
    if (this.fileName) return this.fileName;
    return this.withheld ? 'A source you no longer have access to' : 'Untitled source';
  }

  /**
   * The page as a reader finds it in the document: the number printed on it when the document
   * gives one, the physical page otherwise, and nothing for a source with no pages.
   *
   * A printed label is what the reader looks for on the page itself - "Page iv" of the front
   * matter, "Page A-1" of an appendix - so it is shown in place of the number (#6016). A citation
   * recorded before labels were sent has none and reads as it always did.
   */
  public get pageName(): string | undefined {
    if (this.pageLabel) return this.pageLabel;
    return this.page ? String(this.page) : undefined;
  }

  /** The short name of what kind of thing the source is, such as "File" or "Forum message". */
  public get sourceKindLabel(): string | undefined {
    return this.sourceKind ? CITATION_SOURCE_KIND_LABELS[this.sourceKind] : undefined;
  }

  /** Where in the source the text is, as a reader would say it: "Sheet: Rates 2026". */
  public get locatorName(): string | undefined {
    return this.locator && this.locatorKind ? `${this.locatorKind}: ${this.locator}` : undefined;
  }

  /** Whether some of the quoted text was recognised by OCR rather than read from the file's text. */
  public get isRecognizedText(): boolean {
    return this.textSource === CitationTextSource.Ocr || this.textSource === CitationTextSource.Mixed;
  }

  /** How the text was read, said only when OCR read some of it; text from the file says nothing. */
  public get textSourceName(): string | undefined {
    if (this.textSource === CitationTextSource.Ocr) return "Read by OCR";
    return this.textSource === CitationTextSource.Mixed ? "Partly read by OCR" : undefined;
  }

  /**
   * Whether the excerpt quotes a figure that OCR may have misread.
   *
   * OCR flags the pages holding a number it read with low confidence, not the number itself, so
   * an excerpt is flagged when it quotes any figure from such pages. One that quotes no figure has
   * nothing to misread.
   */
  public get quotesUncertainFigure(): boolean {
    if (!this.isRecognizedText || this.ocrUncertainNumbers !== true) return false;
    return QUOTED_FIGURE.test((this.text ?? "").replace(MARKUP, ""));
  }

  /**
   * Where "Open source" goes: the source's page and, for a knowledge base section, the heading it
   * is under, so the article opens there (#6019).
   */
  public get sourceDestination(): string | undefined {
    if (!this.sourcePath) return undefined;
    const anchor = this.sourceKind === CitationSourceKind.KnowledgeBaseArticle
      && this.locatorKind === CitationLocatorKind.Section
      ? articleHeadingAnchor(this.locator)
      : undefined;
    return anchor ? `${this.sourcePath}#${anchor}` : this.sourcePath;
  }

  /**
   * A stable React key: two chunks of one document share a file ID but not a node ID.
   *
   * The empty check is load-bearing. An empty Guid is a truthy object, so a citation whose
   * node id the API could not parse used to key on the nil GUID - and every such citation
   * in one answer then shared it, which is a duplicate-key collision rather than a missing
   * key. The ordinal is unique within a turn, so it is what identifies those.
   */
  public get key(): string {
    return this.nodeId && !this.nodeId.isEmpty
      ? this.nodeId.value
      : `${this.fileId.value}-${this.ordinal}`;
  }

  /**
   * Which resource the cited span is from, so the sources list can show the spans of one document
   * as one row (#5988). Each retrieved span is its own citation, so two chunks of one file used to
   * read as two rows with the same name.
   *
   * The file id is the resource's id whatever kind of thing it is. A withheld citation the reader
   * cannot see at all carries no ids, and is its own resource: nothing says what it shares.
   */
  public get resourceKey(): string {
    if (!this.fileId.isEmpty) return `file:${this.fileId.value}`;
    if (this.downloadDocumentId) return `document:${this.downloadDocumentId.value}`;
    if (this.sourcePath) return `path:${this.sourcePath}`;
    return `citation:${this.ordinal}`;
  }

  public constructor(params: AssistantCitationParams) {
    this.ordinal = params.ordinal;
    this.fileId = params.fileId;
    this.fileName = params.fileName;
    this.downloadDocumentId = params.downloadDocumentId;
    this.sourcePath = params.sourcePath;
    this.page = params.page;
    this.pageLabel = params.pageLabel;
    this.sourceKind = params.sourceKind;
    this.locator = params.locator;
    this.locatorKind = params.locatorKind;
    this.textSource = params.textSource;
    this.ocrConfidence = params.ocrConfidence;
    this.ocrUncertainNumbers = params.ocrUncertainNumbers;
    this.nodeId = params.nodeId;
    this.matched = params.matched;
    this.text = params.text;
    this.withheld = params.withheld;
    this.retrieverScore = params.retrieverScore;
    this.semanticScore = params.semanticScore;
  }

  public toJSON(): AssistantCitationJSON {
    return AssistantCitationSchema.serialize(this);
  }
  
  public toString(): string {
    return `
      Ordinal: ${this.ordinal},
      File ID: ${this.fileId}, 
      File Name: ${this.fileName},
      Page: ${this.page},
      Node ID: ${this.nodeId},
      Matched: ${this.matched},
      Text: ${this.text}
    `;
  }
}
